import { gql } from "@medbillai/generated-graphql-types";

export const getIssuesQueryV2 = gql(/* GraphQL */ `
  query QueryIssuesV2(
    $closed: Boolean
    $unread: Boolean
    $read: Boolean
    $open: Boolean
    $first: Int
    $after: Int
  ) {
    queryIssuesV2(
      closed: $closed
      unread: $unread
      read: $read
      open: $open
      first: $first
      after: $after
    ) {
      __typename
      id
      title
      openedAt
      closedAt
      lastEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
        ... on issue__ConnectInsuranceRequestedEvent_Type {
          insuranceLinkMessage
        }
        ... on issue__UserSelectionRequestedEvent_Type {
          stringMessage: message
        }
        ... on issue__UserMultiSelectionRequestedEvent_Type {
          multiMessage: message
        }
      }
      firstUnseenEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
        ... on issue__ConnectInsuranceRequestedEvent_Type {
          insuranceLinkMessage
        }
        ... on issue__UserSelectionRequestedEvent_Type {
          stringMessage: message
        }
        ... on issue__UserMultiSelectionRequestedEvent_Type {
          multiMessage: message
        }
      }
    }
  }
`);

export const openNewIssueFromPreviewMutation = gql(/* GraphQL */ `
  mutation OpenNewIssueFromPreview(
    $input: OpenNewIssueFromPreviewInput!
    $benefitsSummarySchemaVersion: Int
    $userId: ID!
  ) {
    openNewIssueFromPreview(input: $input) {
      __typename
      id
      title
      openedAt
      closedAt
      caseType
      providerDetail {
        kind
      }
      benefitsQuestionType
      billDisputeType
      additionalDetails
      benefitsInclude
      benefitsPrice
      benefitsCoverage
      totalBillDollarAmount
      providerName
      labels
      isEscalated
      completedPostEscalationActions
      hasStaffParticipant
      userVisibleEvents(order: { sentAt: { dir: ASC } }) {
        # __typename seems to be needed for the polymorphic query here.
        __typename
        id
        eventType
        participant
        sentAt
        userVisible
        sentByProfile {
          name
        }
        eventFeedbacks (filter:  {owner: {id: {eq: $userId}}}) {
          id
          rating
        }

        ... on issue__BillPayRequestedEvent_Type {
          # Aliased to disambiguate from BillPaymentRequestedEvent.acceptedAt;
          # gel-graphql collapses same-named fields across inline fragments to
          # a single EdgeQL projection, returning null for the wrong subtype.
          billPayAcceptedAt: acceptedAt
          declinedAt
          availableMinimumDiscountOptions
        }
        ... on issue__BillPaySuccessEvent_Type {
          negotiatedBillAmount
          originalBillAmount
          successFeeAmount
          totalCharged
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          billPaymentAcceptedAt: acceptedAt
          billPaid
        }
        ... on issue__BillPaymentSuccessEvent_Type {
          stripePaymentIntentId
          billPaymentRequest {
            id
          }
        }
        ... on issue__DocumentUploadedEvent_Type {
          document {
            id
            name
            dataType
            createdAt
            latestIngestion {
              endedAt
              resources(filter: { isLatest: { eq: true } }, first: 1) {
                __typename
                id
                ... on fhirext__InvoiceVersion_Type {
                  invoiceTitle: title
                }
                ... on fhirext__ExplanationOfBenefitVersion_Type {
                  eobTitle: title
                }
              }
            }
            files {
              id
              sizeBytes
              contentType
            }
          }
        }
        ... on issue__DocumentClassificationFailedEvent_Type {
          classificationStatus
        }
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__HipaaReleaseRequestedEvent_Type {
          hipaaRelease {
            id
            name
            dateOfBirth
            createdAt
            grantedAt
          }
        }
        ... on issue__BillDisputeEvent_Type {
          eobId
          eob {
            id
            resourceId
            provider {
              display
            }
            facility {
              display
            }
            serviceDate
            decision {
              text
              coding {
                code
              }
            }
            findings(
              filter: {
                and: [
                  {
                    explanation: { exists: true }
                    lastReviewStatus: { eq: ACCEPTED }
                    userDismissed: { eq: false }
                  }
                ]
              }
            ) {
              id
              type
            }
          }
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          release {
            id
            createdAt
            grantedAt
          }
          template {
            id
            templateType
            displayName
            description
          }
        }
        ... on issue__ClosedEvent_Type {
          feedback {
            id
            rating
          }
        }
        ... on issue__PaymentAgreementRequestedEvent_Type {
          userAccepted
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
          chargeDate
        }
        ... on issue__BenefitsSummaryGeneratedEvent_Type {
          id
          sentAt
          plan {
            id
            planLabel
            link {
              id
              endpoint {
                id
                slug
              }
            }
            benefitsSummaryResults(
              filter: { schemaVersion: { eq: $benefitsSummarySchemaVersion } }
            ) {
              id
              summary
            }
          }
        }
        ... on issue__PlanSelectionEvent_Type {
          id
          selectedPlan {
            id
          }
          options {
            id
            planLabel
          }
        }
        ... on issue__UserSelectionRequestedEvent_Type {
          id
          message
          stringOptions: options
        }
        ... on issue__ConnectInsuranceRequestedEvent_Type {
          id
          sentAt
          insuranceLinkMessage
        }
        ... on issue__ConnectAccountRequestedEvent_Type {
          id
          sentAt
          connectAccountMessage: chatBody
          endpointType
          buttonText
          showSkipButton
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
          errorMessage
          errorIsRetriable
        }
        ... on issue__PostEscalationActionsEvent_Type {
          actions {
            __typename
            isRequired
            ... on issue__SignTemplatePostEscalationAction_Type {
              signLinkedEvent: linkedEvent {
                id
                release {
                  id
                }
                template {
                  id
                  templateType
                  displayName
                  description
                }
              }
            }
            ... on issue__PaymentAgreementPostEscalationAction_Type {
              payLinkedEvent: linkedEvent {
                id
                userAccepted
              }
            }
            ... on issue__BillPayPostEscalationAction_Type {
              billPayLinkedEvent: linkedEvent {
                id
                acceptedAt
                declinedAt
                availableMinimumDiscountOptions
              }
            }
            ... on issue__ConnectInsurancePostEscalationAction_Type {
              insLinkedEvent: linkedEvent {
                id
                endpointType
              }
            }
            ... on issue__ConnectEhrPostEscalationAction_Type {
              ehrLinkedEvent: linkedEvent {
                id
                endpointType
              }
            }
          }
        }
      }
      lastEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
        ... on issue__ClosurePaymentRequestedEvent_Type {
          dollarAmountSaved
          totalBillDollarAmount
          successFee
          chargeDelayDays
          sentAt
        }
        ... on issue__DocumentAnalysisEvent_Type {
          status
        }
      }
      lastNonUserEvent {
        __typename
        id
        participant
      }
      firstUnseenEvent {
        __typename
        id
        sentAt
        eventType
        participant
        ... on issue__ChatEvent_Type {
          chatBody
        }
        ... on issue__TemplateReleaseRequestedEvent_Type {
          template {
            description
          }
        }
        ... on issue__BillPaymentRequestedEvent_Type {
          acceptedAt
          billPaid
        }
      }
    }
  }
`);
